import {isNil, isNotNil} from 'ramda';

import {omneticApi as api, invalidateOnSuccess} from '@dms/api/base';

import {
  AddVehicleToCampaignApiArg,
  AddVehicleToCampaignApiArgSchema,
  AddVehicleToCampaignApiResponse,
  AddVehicleToCampaignApiResponseSchema,
  CreateCampaignApiArg,
  CreateCampaignApiResponse,
  CreateCampaignApiResponseSchema,
  DeleteCampaignApiArg,
  DeleteCampaignApiResponse,
  DeleteCampaignApiResponseSchema,
  GetCampaignApiArg,
  GetCampaignApiResponse,
  GetCampaignApiResponseSchema,
  ListCampaignsApiResponse,
  ListCampaignsApiResponseSchema,
  ListVehicleCampaignsApiArg,
  ListVehicleCampaignsApiResponse,
  ListVehicleCampaignsApiResponseSchema,
  RemoveVehicleFromCampaignApiArg,
  RemoveVehicleFromCampaignApiArgSchema,
  RemoveVehicleFromCampaignApiResponse,
  RemoveVehicleFromCampaignApiResponseSchema,
  ReorderCampaignsApiArg,
  ReorderCampaignsApiResponse,
  ReorderCampaignsApiResponseSchema,
  UpdateCampaignApiArg,
  UpdateCampaignApiResponse,
  UpdateCampaignApiResponseSchema,
} from './types';

const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    listCampaigns: build.query<ListCampaignsApiResponse, void>({
      query: () => ({
        url: `/dms/v1/campaign`,
      }),
      extraOptions: {
        responseSchema: ListCampaignsApiResponseSchema,
      },
      providesTags: [{type: 'Campaign', id: 'LIST'}],
    }),
    getCampaign: build.query<GetCampaignApiResponse, GetCampaignApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/campaign/${queryArg.id}`,
      }),
      extraOptions: {
        responseSchema: GetCampaignApiResponseSchema,
      },
      providesTags: (result, error, queryArg) => [{type: 'Campaign', id: queryArg.id}],
    }),
    createCampaign: build.mutation<CreateCampaignApiResponse, CreateCampaignApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/campaign`,
        method: 'POST',
        body: queryArg.createCampaignRequestBody,
      }),
      extraOptions: {
        responseSchema: CreateCampaignApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess([{type: 'Campaign', id: 'LIST'}]),
    }),
    updateCampaign: build.mutation<UpdateCampaignApiResponse, UpdateCampaignApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/campaign/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateCampaignRequestBody,
      }),
      extraOptions: {
        responseSchema: UpdateCampaignApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess((result, queryArg: UpdateCampaignApiArg) => [
        {type: 'Campaign', id: queryArg.id},
        {type: 'Campaign', id: 'LIST'},
      ]),
    }),
    reorderCampaigns: build.mutation<ReorderCampaignsApiResponse, ReorderCampaignsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/campaign/positions`,
        method: 'PUT',
        body: queryArg.reorderCampaignsRequestBody,
      }),
      onQueryStarted(queryArg, {dispatch, queryFulfilled}) {
        const {positions} = queryArg.reorderCampaignsRequestBody;

        dispatch(
          injectedRtkApi.util.updateQueryData('listCampaigns', undefined, (draft) => {
            const reorderedCampaigns = [...positions]
              .sort((a, b) => a.position - b.position)
              .map(({campaignId, position}) => {
                const campaign = draft.find((item) => item.id === campaignId);

                return isNil(campaign) ? undefined : {...campaign, position};
              })
              .filter((campaign): campaign is (typeof draft)[number] => isNotNil(campaign));

            // A stale cache (campaign added or removed elsewhere) must not drop cards.
            return reorderedCampaigns.length === draft.length ? reorderedCampaigns : draft;
          })
        );
        queryFulfilled.catch(() => {
          dispatch(injectedRtkApi.util.invalidateTags([{type: 'Campaign', id: 'LIST'}]));
        });
      },
      extraOptions: {
        responseSchema: ReorderCampaignsApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess([{type: 'Campaign', id: 'LIST'}]),
    }),
    deleteCampaign: build.mutation<DeleteCampaignApiResponse, DeleteCampaignApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/campaign/${queryArg.id}`,
        method: 'DELETE',
        params: {republishAds: queryArg.republishAds},
      }),
      extraOptions: {
        responseSchema: DeleteCampaignApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess((result, queryArg: DeleteCampaignApiArg) => [
        {type: 'Campaign', id: queryArg.id},
        {type: 'Campaign', id: 'LIST'},
      ]),
    }),
    addVehicleToCampaign: build.mutation<
      AddVehicleToCampaignApiResponse,
      AddVehicleToCampaignApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/campaign/${queryArg.campaignId}/vehicle/${queryArg.vehicleId}`,
        method: 'PUT',
        params: {republishAds: queryArg.republishAds},
      }),
      extraOptions: {
        requestSchema: AddVehicleToCampaignApiArgSchema,
        responseSchema: AddVehicleToCampaignApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess((result, queryArg: AddVehicleToCampaignApiArg) => [
        {type: 'Campaign', id: queryArg.campaignId},
        {type: 'Campaign', id: `VEHICLE-${queryArg.vehicleId}`},
      ]),
    }),
    removeVehicleFromCampaign: build.mutation<
      RemoveVehicleFromCampaignApiResponse,
      RemoveVehicleFromCampaignApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/campaign/${queryArg.campaignId}/vehicle/${queryArg.vehicleId}`,
        method: 'DELETE',
        params: {republishAds: queryArg.republishAds},
      }),
      extraOptions: {
        requestSchema: RemoveVehicleFromCampaignApiArgSchema,
        responseSchema: RemoveVehicleFromCampaignApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess((result, queryArg: RemoveVehicleFromCampaignApiArg) => [
        {type: 'Campaign', id: queryArg.campaignId},
        {type: 'Campaign', id: `VEHICLE-${queryArg.vehicleId}`},
      ]),
    }),
    listVehicleCampaigns: build.query<ListVehicleCampaignsApiResponse, ListVehicleCampaignsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/campaign/by-vehicle/${queryArg.vehicleId}`,
      }),
      extraOptions: {
        responseSchema: ListVehicleCampaignsApiResponseSchema,
      },
      providesTags: (result, error, queryArg) => [
        {type: 'Campaign', id: `VEHICLE-${queryArg.vehicleId}`},
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListCampaignsQuery,
  useGetCampaignQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useReorderCampaignsMutation,
  useDeleteCampaignMutation,
  useAddVehicleToCampaignMutation,
  useRemoveVehicleFromCampaignMutation,
  useListVehicleCampaignsQuery,
} = injectedRtkApi;
